import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  ArrowRightLeft,
  Boxes,
  Calculator,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  FileCheck,
  FileSpreadsheet,
  Files,
  Gauge,
  HandCoins,
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  Plus,
  Receipt,
  Search,
  Settings,
  ShieldCheck,
  Truck,
  Users,
  Warehouse,
  X
} from 'lucide-react';
import { collection, query, getDocs, limit, orderBy, where } from 'firebase/firestore';
import useClickOutside from './hooks/useClickOutside.js';
import useFirestoreCount from './hooks/useFirestoreCount.js';
import Logo from './NexriseLogo.png';
import { TransactionModal, AccountsInvoiceModal } from './AccountsModals.js';
import CountBadge from './components/CountBadge.js';
import {
  canCreateSalesDocuments,
  canSearchCollection,
  hasOrderApprovalAccess,
  isAccountsUser,
  isAdminUser,
  isOpsManager,
  isOpsUser,
  isSalesUser
} from './accessControl.js';
import {
  createNavBadgeQueries,
  NAV_BADGE_KEYS,
  resolveNavBadgeCount
} from './navigationBadges.js';

const PAYMENT_REQUESTS_DEVELOPMENT_MESSAGE = 'Module development work in progress!';

const Layout = ({ children, user, handleLogout, db, buildVersion }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState({
    quotes: [],
    invoices: [],
    orders: [],
    accounts_invoices: [],
    accounts_transactions: []
  });
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useClickOutside(() => setShowResults(false));

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const sidebarRef = useClickOutside(() => {
    if (windowWidth <= 768) setIsSidebarOpen(false);
  });

  const [showSalesQuickActions, setShowSalesQuickActions] = useState(false);
  const quickActionsRef = useClickOutside(() => setShowSalesQuickActions(false));

  const [showAccTransModal, setShowAccTransModal] = useState(false);
  const [showAccInvModal, setShowAccInvModal] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    Outwards: true,
    Inwards: true,
    Inventory: true,
    Accounts: true,
    Products: true,
    Sales: true
  });

  const toggleSection = (name) => {
    setExpandedSections((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const shouldTrackOpsCounts = isOpsUser(user) || isAdminUser(user) || isSalesUser(user);
  const shouldTrackSalesApprovalCount = hasOrderApprovalAccess(user);
  const shouldTrackInvoicePendingCount = isAccountsUser(user) || isAdminUser(user);
  const badgeQueries = useMemo(() => createNavBadgeQueries(db), [db]);

  const bomDraftCount = useFirestoreCount(
    badgeQueries[NAV_BADGE_KEYS.BOM_DRAFT],
    shouldTrackOpsCounts
  );
  const bomPendingOpsApprovalCount = useFirestoreCount(
    badgeQueries[NAV_BADGE_KEYS.BOM_PENDING_OPS_APPROVAL],
    shouldTrackOpsCounts
  );
  const deliveryChallanOpsCount = useFirestoreCount(
    badgeQueries[NAV_BADGE_KEYS.DELIVERY_CHALLAN_OPS],
    shouldTrackOpsCounts
  );
  const deliveryChallanWarehouseCount = useFirestoreCount(
    badgeQueries[NAV_BADGE_KEYS.DELIVERY_CHALLAN_WAREHOUSE],
    shouldTrackOpsCounts
  );
  const packagingAndStagingOpsCount = useFirestoreCount(
    badgeQueries[NAV_BADGE_KEYS.PACKAGING_AND_STAGING_OPS],
    shouldTrackOpsCounts
  );
  const packagingAndStagingWarehouseCount = useFirestoreCount(
    badgeQueries[NAV_BADGE_KEYS.PACKAGING_AND_STAGING_WAREHOUSE],
    shouldTrackOpsCounts
  );
  const qualityCheckWarehouseCount = useFirestoreCount(
    badgeQueries[NAV_BADGE_KEYS.QUALITY_CHECK_WAREHOUSE],
    shouldTrackOpsCounts
  );
  const inTransitCount = useFirestoreCount(
    badgeQueries[NAV_BADGE_KEYS.IN_TRANSIT],
    shouldTrackOpsCounts
  );
  const orderQueCount = useFirestoreCount(
    badgeQueries[NAV_BADGE_KEYS.ORDER_QUEUE],
    shouldTrackOpsCounts
  );
  const salesApprovalCount = useFirestoreCount(
    badgeQueries[NAV_BADGE_KEYS.SALES_APPROVAL],
    shouldTrackSalesApprovalCount
  );
  const invoicePendingCount = useFirestoreCount(
    badgeQueries[NAV_BADGE_KEYS.INVOICE_PENDING],
    shouldTrackInvoicePendingCount
  );
  const creditApprovalCount = useFirestoreCount(
    badgeQueries[NAV_BADGE_KEYS.CREDIT_APPROVAL],
    shouldTrackInvoicePendingCount
  );

  const navBadgeCounts = {
    [NAV_BADGE_KEYS.BOM_DRAFT]: bomDraftCount,
    [NAV_BADGE_KEYS.BOM_PENDING_OPS_APPROVAL]: bomPendingOpsApprovalCount,
    [NAV_BADGE_KEYS.PACKAGING_AND_STAGING_OPS]: packagingAndStagingOpsCount,
    [NAV_BADGE_KEYS.PACKAGING_AND_STAGING_WAREHOUSE]: packagingAndStagingWarehouseCount,
    [NAV_BADGE_KEYS.DELIVERY_CHALLAN_OPS]: deliveryChallanOpsCount,
    [NAV_BADGE_KEYS.DELIVERY_CHALLAN_WAREHOUSE]: deliveryChallanWarehouseCount,
    [NAV_BADGE_KEYS.QUALITY_CHECK_WAREHOUSE]: qualityCheckWarehouseCount,
    [NAV_BADGE_KEYS.IN_TRANSIT]: inTransitCount,
    [NAV_BADGE_KEYS.ORDER_QUEUE]: orderQueCount,
    [NAV_BADGE_KEYS.SALES_APPROVAL]: salesApprovalCount,
    [NAV_BADGE_KEYS.CREDIT_APPROVAL]: creditApprovalCount,
    [NAV_BADGE_KEYS.INVOICE_PENDING]: invoicePendingCount
  };

  const getBadgeCount = (badgeKey) => resolveNavBadgeCount({
    badgeKey,
    countsByKey: navBadgeCounts,
    role: user?.role
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 768) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (windowWidth <= 768) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, windowWidth]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.length >= 2) {
        performSearch();
      } else {
        setResults({
          quotes: [],
          invoices: [],
          orders: [],
          accounts_invoices: [],
          accounts_transactions: []
        });
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const performSearch = async () => {
    setIsSearching(true);
    setShowResults(true);
    const term = searchTerm.toLowerCase();

    try {
      const allCollections = ['quotes', 'invoices', 'orders', 'accounts_invoices', 'accounts_transactions'];
      const searchCollections = allCollections.filter((colName) => canSearchCollection(user, colName));
      const searchResults = { quotes: [], invoices: [], orders: [], accounts_invoices: [], accounts_transactions: [] };

      for (const colName of searchCollections) {
        const collectionRef = collection(db, colName);
        const q = colName === 'quotes' && user?.role !== 'admin'
          ? query(collectionRef, where('ownerUid', '==', user?.uid), limit(100))
          : query(collectionRef, orderBy('createdAt', 'desc'), limit(100));
        const snapshot = await getDocs(q);
        const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        searchResults[colName] = docs.filter((doc) => {
          if (colName === 'accounts_invoices') {
            return doc.customerName?.toLowerCase().includes(term) ||
              doc.invoiceNo?.toLowerCase().includes(term);
          }
          if (colName === 'accounts_transactions') {
            return doc.category?.toLowerCase().includes(term) ||
              doc.entityName?.toLowerCase().includes(term) ||
              doc.remark?.toLowerCase().includes(term) ||
              doc.invoiceNo?.toLowerCase().includes(term);
          }
          return doc.customerName?.toLowerCase().includes(term) ||
            doc.quoteNumber?.toLowerCase().includes(term) ||
            doc.voucherNo?.toLowerCase().includes(term) ||
            doc.orderNumber?.toLowerCase().includes(term) ||
            doc.subject?.toLowerCase().includes(term);
        }).slice(0, 5);
      }

      setResults(searchResults);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultClick = (path) => {
    setSearchTerm('');
    setShowResults(false);
    navigate(path);
  };

  const menuItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/', roles: ['sales', 'sales_manager', 'ops', 'ops_manager', 'warehouse'] },
    { name: 'Quotes', icon: FileSpreadsheet, path: '/quotes', roles: ['sales', 'sales_manager'] },
    { name: 'Invoices', icon: Receipt, path: '/invoices', roles: ['sales', 'sales_manager'] },
    { name: 'Orders Hub', icon: Files, path: '/orders', roles: ['sales', 'sales_manager', 'ops', 'ops_manager'] },
    { name: 'Order Approval', icon: FileCheck, path: '/sales/order-approvals', roles: ['sales_manager'], badgeKey: NAV_BADGE_KEYS.SALES_APPROVAL },
    {
      type: 'section',
      name: 'Outwards',
      roles: ['ops', 'ops_manager', 'warehouse'],
      children: [
        { name: 'Dispatch Que', icon: ClipboardList, path: '/orders/confirmed', roles: ['sales', 'sales_manager', 'ops', 'ops_manager', 'admin'], badgeKey: NAV_BADGE_KEYS.ORDER_QUEUE },
        { name: 'Bill of Material', icon: ClipboardCheck, path: '/ops/shipments', roles: ['ops', 'ops_manager', 'admin'], badgeKey: NAV_BADGE_KEYS.BILL_OF_MATERIAL },
        { name: 'Packaging & Staging', icon: Package, path: '/ops/packing', roles: ['ops', 'ops_manager', 'warehouse', 'admin'], badgeKey: NAV_BADGE_KEYS.PACKAGING_AND_STAGING },
        { name: 'Delivery Challan', icon: Truck, path: '/ops/ready-to-dispatch', roles: ['ops', 'ops_manager', 'warehouse', 'admin'], badgeKey: NAV_BADGE_KEYS.DELIVERY_CHALLAN },
        { name: 'Quality Check', icon: ShieldCheck, path: '/ops/quality-check', roles: ['ops', 'ops_manager', 'warehouse', 'admin'], badgeKey: NAV_BADGE_KEYS.QUALITY_CHECK },
        { name: 'In Transit', icon: Truck, path: '/ops/in-transit', roles: ['ops', 'ops_manager', 'admin'], badgeKey: NAV_BADGE_KEYS.IN_TRANSIT },
        { name: 'Delivered', icon: Home, path: '/ops/delivered', roles: ['ops', 'ops_manager', 'admin'] }
      ]
    },
    {
      type: 'section',
      name: 'Inwards',
      roles: ['ops', 'ops_manager', 'warehouse'],
      children: [
        { name: 'Inwards Log', icon: ClipboardList, path: '/ops/inwards-log', roles: ['ops', 'ops_manager', 'warehouse'] },
        { name: 'Pickup Challan', icon: Truck, path: '/ops/pickup-challan', roles: ['ops', 'ops_manager', 'warehouse'] },
        { name: 'Material In Checklist', icon: ClipboardCheck, path: '/ops/mic', roles: ['ops', 'ops_manager', 'warehouse'] },
        { name: 'Quality Check / Gate Pass', icon: ShieldCheck, path: '/ops/inbound-qc', roles: ['ops', 'ops_manager', 'warehouse'] },
        { name: 'Received', icon: Home, path: '/ops/received', roles: ['ops', 'ops_manager', 'warehouse'] }
      ]
    },
    {
      type: 'section',
      name: 'Inventory',
      roles: ['ops', 'ops_manager', 'warehouse'],
      children: [
        { name: 'Inventory', icon: Boxes, path: '/ops/inventory', roles: ['ops', 'ops_manager', 'warehouse'] },
        { name: 'Inventory Transfer', icon: ArrowRightLeft, path: '/ops/inventory-transfer', roles: ['ops', 'ops_manager', 'warehouse'] }
      ]
    },
    { name: 'Accounts Dashboard', icon: LayoutDashboard, path: '/', roles: ['accounts'] },
    {
      type: 'section',
      name: 'Sales',
      roles: ['accounts'],
      children: [
        { name: 'Credit Approval', icon: CreditCard, path: '/accounts/approvals', roles: ['accounts'], badgeKey: NAV_BADGE_KEYS.CREDIT_APPROVAL },
        { name: 'Order Invoices', icon: Files, path: '/accounts/orders', roles: ['accounts'], badgeKey: NAV_BADGE_KEYS.INVOICE_PENDING },
        { name: 'Pickup Challans', icon: Truck, path: '/accounts/pickup-challan', roles: ['accounts'] }
      ]
    },
    { name: 'Transaction Ledger', icon: HandCoins, path: '/accounts/transactions', roles: ['accounts'] },
    { name: 'Accounts Invoices', icon: Receipt, path: '/accounts/invoices', roles: ['accounts'] },
    { name: 'Delivered', icon: Home, path: '/ops/delivered', roles: ['sales', 'sales_manager', 'accounts'] },
    { type: 'divider', name: 'sales-mastercalc-divider', roles: ['sales', 'sales_manager'] },
    { name: 'MasterCalc', icon: Calculator, path: '/sales/mastercalc', roles: ['sales', 'sales_manager'] },
    { name: 'Admin Dashboard', icon: Gauge, path: '/admin', roles: ['admin'] },
    {
      type: 'section',
      name: 'Sales',
      roles: ['admin'],
      children: [
        { name: 'Quotes', icon: FileSpreadsheet, path: '/quotes', roles: ['admin'] },
        { name: 'Orders Hub', icon: Files, path: '/orders', roles: ['admin'] },
        { name: 'Invoices', icon: Receipt, path: '/invoices', roles: ['admin'] }
      ]
    },
    {
      type: 'section',
      name: 'Accounts',
      roles: ['admin'],
      children: [
        { name: 'Credit Approval', icon: CreditCard, path: '/accounts/approvals', roles: ['admin'] },
        { name: 'Orders (Finance View)', icon: Files, path: '/accounts/orders', roles: ['admin'], badgeKey: NAV_BADGE_KEYS.INVOICE_PENDING },
        { name: 'Transaction Ledger', icon: HandCoins, path: '/accounts/transactions', roles: ['admin'] },
        { name: 'Accounts Invoices', icon: Receipt, path: '/accounts/invoices', roles: ['admin'] }
      ]
    },
    {
      type: 'section',
      name: 'Outwards',
      roles: ['admin'],
      children: [
        { name: 'Dispatch Que', icon: ClipboardList, path: '/orders/confirmed', roles: ['admin'], badgeKey: NAV_BADGE_KEYS.ORDER_QUEUE },
        { name: 'Bill of Material', icon: ClipboardCheck, path: '/ops/shipments', roles: ['admin'], badgeKey: NAV_BADGE_KEYS.BILL_OF_MATERIAL },
        { name: 'Packaging & Staging', icon: Package, path: '/ops/packing', roles: ['admin'], badgeKey: NAV_BADGE_KEYS.PACKAGING_AND_STAGING },
        { name: 'Delivery Challan', icon: Truck, path: '/ops/ready-to-dispatch', roles: ['admin'], badgeKey: NAV_BADGE_KEYS.DELIVERY_CHALLAN },
        { name: 'Quality Check', icon: ShieldCheck, path: '/ops/quality-check', roles: ['admin'], badgeKey: NAV_BADGE_KEYS.QUALITY_CHECK },
        { name: 'In Transit', icon: Truck, path: '/ops/in-transit', roles: ['admin'], badgeKey: NAV_BADGE_KEYS.IN_TRANSIT },
        { name: 'Delivered', icon: Home, path: '/ops/delivered', roles: ['admin'] }
      ]
    },
    {
      type: 'section',
      name: 'Inwards',
      roles: ['admin'],
      children: [
        { name: 'Inwards Log', icon: ClipboardList, path: '/ops/inwards-log', roles: ['admin'] },
        { name: 'Pickup Challan', icon: Truck, path: '/ops/pickup-challan', roles: ['admin'] },
        { name: 'Material In Checklist', icon: ClipboardCheck, path: '/ops/mic', roles: ['admin'] },
        { name: 'Quality Check / Gate Pass', icon: ShieldCheck, path: '/ops/inbound-qc', roles: ['admin'] },
        { name: 'Received', icon: Home, path: '/ops/received', roles: ['admin'] }
      ]
    },
    {
      type: 'section',
      name: 'Products',
      roles: ['admin'],
      children: [
        { name: 'Product Catalog', icon: Package, path: '/admin/products', roles: ['admin'] },
        { name: 'MasterCalc', icon: Calculator, path: '/admin/products/mastercalc', roles: ['admin'] }
      ]
    },
    { name: 'Warehouse', icon: Warehouse, path: '/admin/warehouses', roles: ['admin'] },
    { name: 'Employees', icon: Users, path: '/admin/employees', roles: ['admin'] },
    { type: 'divider', name: 'admin-settings-divider', roles: ['admin'] },
    { name: 'System Settings', icon: Settings, path: '/admin/settings', roles: ['admin'] },
    {
      type: 'section',
      name: 'Payment Requests',
      roles: ['sales', 'sales_manager', 'ops', 'ops_manager', 'warehouse', 'accounts', 'admin'],
      children: [
        { name: 'My Requests', icon: HandCoins, path: '/payments-requests', roles: ['sales', 'sales_manager', 'ops', 'ops_manager', 'warehouse', 'accounts', 'admin'], moduleInProgress: true },
        { name: 'For My Approval', icon: FileCheck, path: '/payment-requests/approvals', roles: ['sales_manager', 'ops_manager'], moduleInProgress: true },
        { name: 'All Requests', icon: FileCheck, path: '/payment-requests/accounts-all', roles: ['accounts'], paymentRequestApprovalRoles: ['accounts_check', 'accounts_payment'], moduleInProgress: true },
        { name: 'HR Review', icon: FileCheck, path: '/payment-requests/hr-review', roles: ['admin'], paymentRequestApprovalRole: 'hr_review', moduleInProgress: true },
        { name: 'Admin Approval', icon: FileCheck, path: '/payment-requests/admin-approval', roles: ['admin'], paymentRequestApprovalRole: 'admin_approval', moduleInProgress: true },
        { name: 'Advance Settlements', icon: ClipboardList, path: '/payment-requests/settlements', roles: ['sales', 'sales_manager', 'ops', 'ops_manager', 'warehouse', 'accounts', 'admin'], moduleInProgress: true }
      ]
    }
  ].filter((item) => !item.roles || item.roles.includes(user?.role));

  const resultSectionStyle = {
    padding: '5px 0',
    borderBottom: '1px solid #e5e7eb'
  };

  const resultHeaderStyle = {
    padding: '5px 15px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase'
  };

  const resultItemStyle = {
    padding: '10px 15px',
    cursor: 'pointer'
  };

  const baseActionBtnStyle = {
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  };

  const dropdownLinkStyle = {
    padding: '12px 15px',
    textDecoration: 'none',
    color: '#374151',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderBottom: '1px solid #f3f4f6'
  };

  const collapsibleSectionStyle = {
    overflow: 'hidden',
    transition: 'max-height 0.24s ease, opacity 0.2s ease, transform 0.2s ease',
    transformOrigin: 'top'
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - var(--app-update-height, 0px))', fontFamily: 'Arial, sans-serif', backgroundColor: '#f3f4f6', overflow: 'hidden' }}>
      {windowWidth <= 768 && isSidebarOpen && (
        <div style={{
          position: 'fixed', top: 'var(--app-update-height, 0px)', left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1999
        }} />
      )}

      <div
        ref={sidebarRef}
        style={{
          width: '250px', backgroundColor: '#0b0f14', color: 'white', display: 'flex', flexDirection: 'column',
          position: windowWidth <= 768 ? 'fixed' : 'relative',
          top: windowWidth <= 768 ? 'var(--app-update-height, 0px)' : undefined,
          height: 'calc(100vh - var(--app-update-height, 0px))',
          zIndex: 2000,
          left: isSidebarOpen ? 0 : '-250px',
          transition: 'left 0.3s ease'
        }}
      >
        <div style={{ padding: '20px', borderBottom: '1px solid #1f2937', display: 'flex', justifyContent: 'center', position: 'relative' }}>
          <img src={Logo} alt="NexBook Logo" style={{ maxWidth: '85%', height: 'auto' }} />
          {windowWidth <= 768 && (
            <button onClick={() => setIsSidebarOpen(false)} style={{ position: 'absolute', right: 10, top: 20, background: 'none', border: 'none', color: 'white' }}>
              <X size={20} />
            </button>
          )}
        </div>

        <nav
          className="sidebar-scroll"
          style={{ flex: 1, padding: '10px', overflowY: 'auto' }}
        >
          {menuItems.map((item) => {
            if (item.type === 'divider') {
              return (
                <div
                  key={item.name}
                  style={{
                    height: '1px',
                    backgroundColor: '#1f2937',
                    margin: '10px 15px'
                  }}
                />
              );
            }

            if (item.type === 'section') {
              const isExpanded = expandedSections[item.name];
              const visibleChildren = item.children.filter((child) => {
                const requiredApprovalRoles = child.paymentRequestApprovalRoles
                  || (child.paymentRequestApprovalRole ? [child.paymentRequestApprovalRole] : []);
                return (!child.roles || child.roles.includes(user?.role))
                  && (!requiredApprovalRoles.length || requiredApprovalRoles.some((role) => user?.paymentRequestApprovalRoles?.includes(role)));
              });

              if (visibleChildren.length === 0) return null;

              return (
                <div key={item.name} style={{ marginBottom: '5px' }}>
                  <button
                    onClick={() => toggleSection(item.name)}
                    style={{
                      width: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 15px', background: 'none', border: 'none',
                      color: '#a3aab7', cursor: 'pointer', borderRadius: '6px',
                      transition: 'all 0.2s'
                    }}
                    className="sidebar-item-hover"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{item.name}</span>
                    </div>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <div
                    style={{
                      ...collapsibleSectionStyle,
                      maxHeight: isExpanded ? `${visibleChildren.length * 56}px` : '0px',
                      opacity: isExpanded ? 1 : 0,
                      transform: isExpanded ? 'translateY(0)' : 'translateY(-6px)'
                    }}
                  >
                    <div style={{ paddingLeft: '10px', paddingTop: isExpanded ? '4px' : '0px' }}>
                      {visibleChildren.map((child) => {
                        const isActive = location.pathname === child.path;
                        const ChildIcon = child.icon;

                        return (
                          <Link
                            key={child.name}
                            to={child.path}
                            onClick={(event) => {
                              if (!child.moduleInProgress) return;
                              event.preventDefault();
                              window.alert(PAYMENT_REQUESTS_DEVELOPMENT_MESSAGE);
                            }}
                            className="sidebar-item-hover"
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '10px 15px', textDecoration: 'none',
                              color: isActive ? 'white' : '#a3aab7',
                              backgroundColor: isActive ? '#171c24' : 'transparent',
                              borderRadius: '6px', marginBottom: '2px',
                              fontSize: '14px',
                              transition: 'all 0.2s'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <ChildIcon size={16} strokeWidth={2} />
                              {child.name}
                            </div>
                            <CountBadge count={getBadgeCount(child.badgeKey)} />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            const isActive = location.pathname === item.path;
            const ItemIcon = item.icon;

            return (
              <Link
                key={item.name}
                to={item.path}
                className="sidebar-item-hover"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 15px', textDecoration: 'none',
                  color: isActive ? 'white' : '#a3aab7',
                  backgroundColor: isActive ? '#171c24' : 'transparent',
                  borderRadius: '6px', marginBottom: '5px',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <ItemIcon size={18} strokeWidth={2} />
                  {item.name}
                </div>
                <CountBadge count={getBadgeCount(item.badgeKey)} />
              </Link>
            );
          })}
        </nav>

        <div style={{ padding: '20px', borderTop: '1px solid #1f2937' }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>
            Build {buildVersion || 'N/A'}
          </div>
          <div style={{ fontSize: '12px', color: '#7c8594', marginBottom: '10px' }}>
            Logged in as: {user?.displayName || user?.email || 'Unknown user'}
          </div>
          <button
            onClick={handleLogout}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'none', border: 'none', color: '#f87171', cursor: 'pointer' }}
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          height: '60px', minHeight: '60px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: windowWidth <= 768 ? '0 15px' : '0 30px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {windowWidth <= 768 && (
              <button
                onClick={() => setIsSidebarOpen(true)}
                style={{ background: 'none', border: 'none', color: '#374151', cursor: 'pointer', padding: '5px' }}
              >
                <Menu size={24} />
              </button>
            )}

            <div style={{ position: 'relative' }} ref={searchRef}>
              <div style={{
                display: 'flex', alignItems: 'center', backgroundColor: '#f3f4f6', padding: '8px 15px', borderRadius: '8px',
                width: windowWidth <= 768 ? (windowWidth <= 480 ? '150px' : '250px') : '400px'
              }}>
                <Search size={18} color="#9ca3af" />
                <input
                  type="text"
                  placeholder={windowWidth <= 480 ? 'Search...' :
                    (isOpsUser(user) ? 'Search orders...' :
                      isAccountsUser(user) ? 'Search transactions, invoices...' :
                        'Search quotes, invoices...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => searchTerm.length >= 2 && setShowResults(true)}
                  style={{ border: 'none', background: 'transparent', marginLeft: '10px', outline: 'none', width: '100%' }}
                />
                {searchTerm && (
                  <X size={14} color="#9ca3af" style={{ cursor: 'pointer' }} onClick={() => setSearchTerm('')} />
                )}
              </div>

              {showResults && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  backgroundColor: 'white', border: '1px solid #e5e7eb',
                  borderRadius: '8px', marginTop: '5px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000, maxHeight: '400px', overflowY: 'auto'
                }}>
                  {isSearching ? (
                    <div style={{ padding: '15px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>Searching...</div>
                  ) : (
                    <>
                      {Object.keys(results).every((key) => results[key].length === 0) ? (
                        <div style={{ padding: '15px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>No results found</div>
                      ) : (
                        <div style={{ padding: '10px 0' }}>
                          {results.quotes.length > 0 && (
                            <div style={resultSectionStyle}>
                              <div style={resultHeaderStyle}>Quotes</div>
                              {results.quotes.map((q) => (
                                <div key={q.id} onClick={() => handleResultClick(`/quotes/${q.id}`)} style={resultItemStyle} className="hover-highlight">
                                  <div style={{ fontWeight: '600' }}>{q.quoteNumber || 'No Number'}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{q.customerName} - {q.subject}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {results.invoices.length > 0 && (
                            <div style={resultSectionStyle}>
                              <div style={resultHeaderStyle}>Invoices</div>
                              {results.invoices.map((i) => (
                                <div key={i.id} onClick={() => handleResultClick(`/invoices/${i.id}`)} style={resultItemStyle} className="hover-highlight">
                                  <div style={{ fontWeight: '600' }}>{i.voucherNo || 'No Number'}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{i.customerName} - {i.subject}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {results.orders.length > 0 && (
                            <div style={resultSectionStyle}>
                              <div style={resultHeaderStyle}>Orders</div>
                              {results.orders.map((o) => (
                                <div key={o.id} onClick={() => handleResultClick(`/orders/${o.id}`)} style={resultItemStyle} className="hover-highlight">
                                  <div style={{ fontWeight: '600' }}>{o.orderNumber || 'No Number'}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{o.customerName} - {o.subject}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {results.accounts_invoices.length > 0 && (
                            <div style={resultSectionStyle}>
                              <div style={resultHeaderStyle}>Accounts Invoices</div>
                              {results.accounts_invoices.map((i) => (
                                <div key={i.id} onClick={() => handleResultClick(`/accounts/invoices?highlight=${i.id}`)} style={resultItemStyle} className="hover-highlight">
                                  <div style={{ fontWeight: '600' }}>{i.invoiceNo}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{i.customerName} - Rs {i.totalAmount?.toLocaleString()}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {results.accounts_transactions.length > 0 && (
                            <div style={resultSectionStyle}>
                              <div style={resultHeaderStyle}>Transactions</div>
                              {results.accounts_transactions.map((t) => (
                                <div key={t.id} onClick={() => handleResultClick(`/accounts/transactions?highlight=${t.id}`)} style={resultItemStyle} className="hover-highlight">
                                  <div style={{ fontWeight: '600' }}>{t.category}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{t.entityName} - Rs {t.amount?.toLocaleString()} ({new Date(t.date).toLocaleDateString()})</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {canCreateSalesDocuments(user) && (
            <div style={{ position: 'relative' }} ref={quickActionsRef}>
              {windowWidth <= 768 ? (
                <>
                  <button
                    onClick={() => setShowSalesQuickActions(!showSalesQuickActions)}
                    className="action-btn-hover"
                    style={{
                      backgroundColor: '#2563eb', color: 'white', border: 'none',
                      padding: '10px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Plus size={20} />
                  </button>

                  {showSalesQuickActions && (
                    <div style={{
                      position: 'absolute', top: '100%', right: 0,
                      backgroundColor: 'white', border: '1px solid #e5e7eb',
                      borderRadius: '8px', marginTop: '5px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000, width: '160px', display: 'flex', flexDirection: 'column', overflow: 'hidden'
                    }}>
                      <Link to="/quotes/new" onClick={() => setShowSalesQuickActions(false)} style={dropdownLinkStyle} className="hover-highlight">
                        <Plus size={14} /> New Quote
                      </Link>
                      <Link to="/invoices/new" onClick={() => setShowSalesQuickActions(false)} style={dropdownLinkStyle} className="hover-highlight">
                        <Plus size={14} /> New Invoice
                      </Link>
                      <Link to="/orders/new" onClick={() => setShowSalesQuickActions(false)} style={dropdownLinkStyle} className="hover-highlight">
                        <Plus size={14} /> New Order
                      </Link>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Link to="/quotes/new" style={{ textDecoration: 'none' }}>
                    <button style={{ ...baseActionBtnStyle, backgroundColor: '#2563eb' }} className="action-btn-hover">
                      <Plus size={16} /> New Quote
                    </button>
                  </Link>
                  <Link to="/invoices/new" style={{ textDecoration: 'none' }}>
                    <button style={{ ...baseActionBtnStyle, backgroundColor: '#10b981' }} className="action-btn-hover">
                      <Plus size={16} /> New Invoice
                    </button>
                  </Link>
                  <Link to="/orders/new" style={{ textDecoration: 'none' }}>
                    <button style={{ ...baseActionBtnStyle, backgroundColor: '#f59e0b' }} className="action-btn-hover">
                      <Plus size={16} /> New Order
                    </button>
                  </Link>
                </div>
              )}
            </div>
          )}

          {isAccountsUser(user) && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setShowAccTransModal(true)}
                style={{ ...baseActionBtnStyle, backgroundColor: '#3b82f6' }}
                className="action-btn-hover"
              >
                <Plus size={16} /> Add Transaction
              </button>
              <button
                onClick={() => setShowAccInvModal(true)}
                style={{ ...baseActionBtnStyle, backgroundColor: '#10b981' }}
                className="action-btn-hover"
              >
                <Plus size={16} /> New Invoice
              </button>
            </div>
          )}
        </div>

        <div style={{ padding: windowWidth <= 768 ? '10px' : '30px' }}>
          {children || <Outlet />}
        </div>
      </div>

      <TransactionModal
        show={showAccTransModal}
        onClose={() => setShowAccTransModal(false)}
        db={db}
        onSave={() => setShowAccTransModal(false)}
      />
      <AccountsInvoiceModal
        show={showAccInvModal}
        onClose={() => setShowAccInvModal(false)}
        db={db}
        onSave={() => setShowAccInvModal(false)}
      />
    </div>
  );
};

export default Layout;
